(() => {
  'use strict';

  const doc = document;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const once = (node, key) => {
    const token = `afReady${key}`;
    if (!node || node.dataset[token]) return false;
    node.dataset[token] = 'true';
    return true;
  };
  const safePlay = (video) => {
    if (!video) return;
    const result = video.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  };

  function splitText(scope = doc) {
    scope.querySelectorAll('[data-af-split]').forEach((element) => {
      if (!once(element, 'Split')) return;
      const text = element.textContent.trim();
      if (!text) return;
      if (!element.hasAttribute('aria-label')) element.setAttribute('aria-label', text);
      element.textContent = '';
      [...text].forEach((character, index) => {
        if (character === ' ') {
          element.appendChild(doc.createTextNode(' '));
          return;
        }
        const span = doc.createElement('span');
        span.className = 'af-char';
        span.setAttribute('aria-hidden', 'true');
        span.style.setProperty('--char-index', index);
        span.textContent = character;
        element.appendChild(span);
      });
    });
  }

  function wrapWords(scope = doc) {
    scope.querySelectorAll('[data-af-word-reveal]').forEach((element) => {
      if (!once(element, 'Words')) return;
      const words = element.textContent.trim().split(/\s+/);
      element.textContent = '';
      words.forEach((word, index) => {
        const span = doc.createElement('span');
        span.className = 'af-word';
        span.textContent = word;
        span.style.setProperty('--word-index', index);
        element.appendChild(span);
        element.appendChild(doc.createTextNode(' '));
      });
    });
  }

  function revealMotion(scope = doc) {
    const elements = [...scope.querySelectorAll('[data-af-reveal], [data-af-split], [data-af-meter], [data-af4-reveal-section]')];
    if (reduced.matches || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    elements.forEach((element) => observer.observe(element));
  }

  function loader() {
    const element = doc.querySelector('[data-af-loader]');
    if (!element) return;
    const started = performance.now();
    let leaving = false;
    const exit = () => {
      if (leaving) return;
      leaving = true;
      const remaining = Math.max(0, 1750 - (performance.now() - started));
      window.setTimeout(() => {
        element.classList.add('is-leaving');
        window.setTimeout(() => element.remove(), 1100);
      }, remaining);
    };
    if (doc.readyState === 'complete') exit();
    else window.addEventListener('load', exit, { once: true });
    window.setTimeout(exit, 3200);
  }

  function mobileMenu() {
    const menu = doc.querySelector('[data-af-mobile-menu]');
    const open = doc.querySelector('[data-af-menu-open]');
    const close = doc.querySelector('[data-af-menu-close]');
    if (!menu || !open || !once(menu, 'Menu')) return;
    const setMenu = (visible) => {
      menu.classList.toggle('is-open', visible);
      menu.setAttribute('aria-hidden', String(!visible));
      open.setAttribute('aria-expanded', String(visible));
      doc.body.classList.toggle('is-menu-open', visible);
      if (visible) close?.focus(); else open.focus();
    };
    open.addEventListener('click', () => setMenu(true));
    close?.addEventListener('click', () => setMenu(false));
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
    doc.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && menu.classList.contains('is-open')) setMenu(false);
    });
  }

  function filmHero(scope = doc) {
    scope.querySelectorAll('[data-af-film-hero]').forEach((hero) => {
      if (!once(hero, 'Hero')) return;
      const stage = hero.querySelector('.af-film-hero__stage');
      const slides = [...hero.querySelectorAll('[data-af-film-slide]')];
      const buttons = [...hero.querySelectorAll('[data-af-scene-target]')];
      const copy = hero.querySelector('[data-af-scene-copy]');
      let scenes = [];
      let active = -1;
      try { scenes = JSON.parse(hero.querySelector('[data-af-scene-data]')?.textContent || '[]'); } catch (_) {}

      const activate = (index) => {
        if (index === active) return;
        active = index;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-current', slideIndex === index);
          const video = slide.querySelector('video');
          if (slideIndex === index && !reduced.matches) safePlay(video); else video?.pause();
        });
        buttons.forEach((button, buttonIndex) => button.classList.toggle('is-active', buttonIndex === index));
        if (copy && scenes[index]) {
          copy.animate?.([{ opacity: .15, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }], { duration: 420, easing: 'cubic-bezier(.2,.75,.18,1)' });
          copy.innerHTML = `<span>0${index + 1} / ${scenes[index].label}</span><p>${scenes[index].copy}</p>`;
        }
      };

      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = hero.getBoundingClientRect();
        const travel = Math.max(1, hero.offsetHeight - window.innerHeight);
        const progress = clamp(-rect.top / travel);
        const revealOne = clamp((progress - .13) / .22);
        const revealTwo = clamp((progress - .48) / .22);
        if (slides[1]) {
          slides[1].style.setProperty('--af-reveal-x', `${(revealOne * 125).toFixed(2)}%`);
          slides[1].style.setProperty('--af-reveal-y', `${(revealOne * 105).toFixed(2)}%`);
          slides[1].style.setProperty('--af-origin-x', `${38 + revealOne * 13}%`);
          slides[1].style.setProperty('--af-origin-y', `${67 - revealOne * 16}%`);
        }
        if (slides[2]) {
          slides[2].style.setProperty('--af-reveal-x', `${(revealTwo * 125).toFixed(2)}%`);
          slides[2].style.setProperty('--af-reveal-y', `${(revealTwo * 105).toFixed(2)}%`);
          slides[2].style.setProperty('--af-origin-x', `${68 - revealTwo * 17}%`);
          slides[2].style.setProperty('--af-origin-y', `${35 + revealTwo * 15}%`);
        }
        slides.forEach((slide, index) => {
          const sceneProgress = clamp(progress * 3 - index, 0, 1);
          slide.style.setProperty('--af-media-scale', (1.06 - sceneProgress * .04).toFixed(4));
        });
        activate(progress < .29 ? 0 : progress < .64 ? 1 : 2);
        stage?.style.setProperty('--af-hero-progress', progress.toFixed(3));
      };
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      buttons.forEach((button) => button.addEventListener('click', () => {
        const index = Number(button.dataset.afSceneTarget || 0);
        const destination = hero.offsetTop + (hero.offsetHeight - window.innerHeight) * ([.03, .38, .74][index] || 0);
        window.scrollTo({ top: destination, behavior: reduced.matches ? 'auto' : 'smooth' });
      }));
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
      activate(0);
      update();
    });
  }

  function wordReveal(scope = doc) {
    const elements = [...scope.querySelectorAll('[data-af-word-reveal]')];
    if (!elements.length) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      elements.forEach((element) => {
        const words = [...element.querySelectorAll('.af-word')];
        const rect = element.getBoundingClientRect();
        const progress = reduced.matches ? 1 : clamp((window.innerHeight * .82 - rect.top) / (window.innerHeight * .66));
        const count = Math.ceil(progress * words.length);
        words.forEach((word, index) => word.classList.toggle('is-lit', index < count));
      });
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();
  }

  function lazyVideos(scope = doc) {
    const videos = [...scope.querySelectorAll('[data-af-video]')].filter((video) => !video.closest('[data-af-film-hero]'));
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > .32 && !reduced.matches) safePlay(video); else video.pause();
        });
      }, { threshold: [0, .33, .75] });
      videos.forEach((video) => observer.observe(video));
    }
    scope.querySelectorAll('[data-af-video-toggle]').forEach((button) => {
      if (!once(button, 'Video')) return;
      const video = button.closest('.af-object-gallery__media')?.querySelector('video');
      if (!video) return;
      button.addEventListener('click', () => {
        if (video.paused) safePlay(video); else video.pause();
        button.classList.toggle('is-playing', !video.paused);
      });
    });
  }

  function gallery(scope = doc) {
    scope.querySelectorAll('[data-af-gallery]').forEach((element) => {
      if (!once(element, 'Gallery')) return;
      const rail = element.querySelector('[data-af-gallery-rail]');
      const progress = element.querySelector('[data-af-gallery-progress]');
      if (!rail) return;
      const step = () => Math.max(280, rail.clientWidth * .72);
      element.querySelector('[data-af-gallery-next]')?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
      element.querySelector('[data-af-gallery-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      const setProgress = () => {
        const maximum = Math.max(1, rail.scrollWidth - rail.clientWidth);
        progress?.style.setProperty('transform', `scaleX(${clamp(rail.scrollLeft / maximum)})`);
      };
      rail.addEventListener('scroll', setProgress, { passive: true });
      let dragging = false;
      let origin = 0;
      let start = 0;
      rail.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button,a')) return;
        dragging = true;
        origin = event.clientX;
        start = rail.scrollLeft;
        rail.classList.add('is-dragging');
        rail.setPointerCapture(event.pointerId);
      });
      rail.addEventListener('pointermove', (event) => { if (dragging) rail.scrollLeft = start - (event.clientX - origin); });
      const stop = () => { dragging = false; rail.classList.remove('is-dragging'); };
      rail.addEventListener('pointerup', stop);
      rail.addEventListener('pointercancel', stop);
      setProgress();
    });
  }

  function ritual(scope = doc) {
    scope.querySelectorAll('[data-af-ritual]').forEach((element) => {
      if (!once(element, 'Ritual')) return;
      const scenes = [...element.querySelectorAll('[data-af-ritual-scene]')];
      const controls = [...element.querySelectorAll('[data-af-ritual-target]')];
      const activate = (index) => {
        scenes.forEach((scene, sceneIndex) => {
          scene.classList.toggle('is-active', sceneIndex === index);
          const video = scene.querySelector('video');
          if (sceneIndex === index && !reduced.matches) safePlay(video); else video?.pause();
        });
        controls.forEach((control, controlIndex) => control.classList.toggle('is-active', controlIndex === index));
      };
      controls.forEach((control) => control.addEventListener('click', () => scenes[Number(control.dataset.afRitualTarget)]?.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' })));
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible) activate(Number(visible.target.dataset.afRitualScene));
        }, { rootMargin: '-20% 0px -25%', threshold: [.15, .4, .7] });
        scenes.forEach((scene) => observer.observe(scene));
      }
      activate(0);
    });
  }

  function lab(scope = doc) {
    scope.querySelectorAll('[data-af-lab]').forEach((element) => {
      if (!once(element, 'Lab')) return;
      const visual = element.querySelector('.af-lab__visual');
      const range = element.querySelector('[data-af-lab-range]');
      const setValue = () => visual?.style.setProperty('--af-compare', `${range?.value || 52}%`);
      range?.addEventListener('input', setValue);
      setValue();
    });
  }

  function parallax(scope = doc) {
    const elements = [...scope.querySelectorAll('[data-af-parallax]')];
    if (!elements.length || reduced.matches) return;
    let ticking = false;
    const update = () => {
      ticking = false;
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        if (rect.bottom < -150 || rect.top > window.innerHeight + 150) return;
        const amount = Number(element.dataset.afParallax || 0);
        const distance = (rect.top + rect.height * .5 - window.innerHeight * .5) * amount * -1;
        element.style.setProperty('--af-parallax-y', `${distance.toFixed(2)}px`);
      });
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    update();
  }

  function commerce(scope = doc) {
    scope.querySelectorAll('[data-af-product-form]').forEach((form) => {
      if (!once(form, 'Cart')) return;
      const productRoot = form.closest('[data-af-product-root],[data-af4-product-root]');
      const select = form.querySelector('[data-af-variant-select]');
      let variants = [];
      try { variants = JSON.parse(productRoot?.querySelector('[data-af-product-json],[data-af4-variants]')?.textContent || '[]'); } catch (_) {}
      select?.addEventListener('change', () => {
        const selected = variants.find((variant) => String(variant.id) === select.value);
        const fallbackPrice = select.selectedOptions[0]?.dataset.price;
        const mobilePrice = productRoot?.querySelector('[data-af-mobile-price]');
        const buttonPrice = productRoot?.querySelector('[data-af-variant-price]');
        const productImage = productRoot?.querySelector('.af-featured__product-image img');
        if (mobilePrice && (selected || fallbackPrice)) mobilePrice.textContent = fallbackPrice || mobilePrice.textContent;
        if (buttonPrice && fallbackPrice) buttonPrice.textContent = fallbackPrice;
        const nextImage = selected?.featured_image?.src || select.selectedOptions[0]?.dataset.image;
        if (productImage && nextImage) {
          productImage.removeAttribute('srcset');
          productImage.src = nextImage;
        }
      });
      form.addEventListener('submit', async (event) => {
        if (event.submitter?.name === 'checkout') return;
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();
        const submit = form.querySelector('[type="submit"]');
        submit?.setAttribute('aria-busy', 'true');
        try {
          const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
          const action = new URL(form.action || `${rootUrl}cart/add`, window.location.origin);
          action.pathname = action.pathname.replace(/\/cart\/add(?:\.js)?$/, '/cart/add.js');
          const response = await fetch(action.toString(), {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: new FormData(form)
          });
          const payload = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this option.');

          const cartResponse = await fetch(`${rootUrl}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
          });
          const cart = cartResponse.ok ? await cartResponse.json() : null;
          const toast = doc.querySelector('[data-af-cart-toast]');
          const toastMessage = toast?.querySelector('span');
          if (toastMessage) toastMessage.textContent = 'Added to your ritual';
          toast?.classList.add('is-visible');
          window.setTimeout(() => toast?.classList.remove('is-visible'), 3600);
          if (cart) {
            doc.querySelectorAll('.af-header__bag span').forEach((badge) => {
              badge.textContent = String(cart.item_count);
              badge.hidden = false;
            });
          }
        } catch (error) {
          const toast = doc.querySelector('[data-af-cart-toast]');
          if (toast) {
            const message = toast.querySelector('span');
            if (message) message.textContent = error.message;
            toast.classList.add('is-visible');
          }
        } finally {
          submit?.removeAttribute('aria-busy');
        }
      });
    });

    scope.querySelectorAll('[data-af-qty-minus],[data-af-qty-plus]').forEach((button) => {
      if (!once(button, 'Qty')) return;
      button.addEventListener('click', () => {
        const input = button.closest('.af-quantity,.af4-quantity')?.querySelector('input');
        if (!input) return;
        const delta = button.hasAttribute('data-af-qty-plus') ? 1 : -1;
        input.value = String(Math.max(Number(input.min || 1), Number(input.value || 1) + delta));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    scope.querySelectorAll('[data-af-product-root],[data-af4-product-root]').forEach((productRoot) => {
      const dock = productRoot.querySelector('[data-af-mobile-buy]');
      const form = productRoot.querySelector('[data-af-product-form]');
      if (!dock || !form || !once(dock, 'Dock')) return;
      dock.querySelector('[data-af-mobile-submit]')?.addEventListener('click', () => form.requestSubmit());
      const update = () => {
        const rect = form.getBoundingClientRect();
        dock.classList.toggle('is-visible', rect.bottom < 0 && productRoot.getBoundingClientRect().bottom > window.innerHeight);
      };
      window.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  function cursor() {
    const element = doc.querySelector('[data-af-cursor]');
    if (!element || reduced.matches || !window.matchMedia('(pointer:fine)').matches) return;
    window.addEventListener('pointermove', (event) => {
      element.classList.add('is-active');
      element.style.transform = `translate3d(${event.clientX}px,${event.clientY}px,0) translate(-50%,-50%)`;
    }, { passive: true });
    doc.querySelectorAll('a,button,input,select,[data-af-gallery-rail]').forEach((target) => {
      target.addEventListener('mouseenter', () => element.classList.add('is-large'));
      target.addEventListener('mouseleave', () => element.classList.remove('is-large'));
    });
  }

  function magnetic(scope = doc) {
    if (reduced.matches || !window.matchMedia('(pointer:fine)').matches) return;
    scope.querySelectorAll('[data-af-magnetic]').forEach((element) => {
      if (!once(element, 'Magnetic')) return;
      element.addEventListener('pointermove', (event) => {
        const rect = element.getBoundingClientRect();
        element.style.transform = `translate(${(event.clientX - rect.left - rect.width / 2) * .12}px,${(event.clientY - rect.top - rect.height / 2) * .12}px)`;
      });
      element.addEventListener('pointerleave', () => { element.style.transform = ''; });
    });
  }

  function boot(scope = doc) {
    splitText(scope);
    wrapWords(scope);
    revealMotion(scope);
    mobileMenu();
    filmHero(scope);
    wordReveal(scope);
    lazyVideos(scope);
    gallery(scope);
    ritual(scope);
    lab(scope);
    parallax(scope);
    commerce(scope);
    magnetic(scope);
  }

  doc.addEventListener('DOMContentLoaded', () => {
    loader();
    boot();
    cursor();
  });
  doc.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
